import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '~/styles/colors';
import { computeEcoDayChart, VentilationWindow } from '../../ventilationChart';

const INFO = colors.state.info.base;
const PRIMARY = colors.brand.primary.base;
const PRIMARY_LIGHT = colors.brand.primary.light;
const PRIMARY_LIGHTER = colors.brand.primary.lighter;
const RED = colors.celsius['danger-base'];
const STROKE = colors.stroke['soft-200'];
const TEXT_SOFT = colors.text['sub-350'];
const TEXT_STRONG = colors.text['main-900'];
const TEXT_SUB = colors.text['sub-500'];
const WARNING = colors.state.warning.base;
const WARNING_DARK = colors.celsius['warning-dark'];
const WARNING_LIGHTER = colors.celsius['warning-lighter'];
const WHITE = colors.bg['white-0'];

interface EcoDayChartProps {
  setpoint: number | null;
  hysteresis: { upper: number | null; lower: number | null };
  /** Desligado, o LTI some do gráfico (faixa, linha, rótulo e legenda). */
  lti: { enabled: boolean; value: number | null };
  ventilation: { start: number; end: number };
  schedule: VentilationWindow;
}

/** Valores do design, usados enquanto o campo do formulário está vazio ou inválido. */
const DESIGN_DEFAULTS = { setpoint: 24, hysteresis: 1, lti: 18 };

/**
 * "Modo ECO ao Longo de um Dia" do design (viewBox 1280 × 700) acompanhando a configuração: a janela vem da
 * programação de hoje, e setpoint, histereses, LTI e ventilação vêm do formulário. As curvas são as do design
 * (ilustrativas), reescaladas para essa janela e para esses níveis.
 */
export function EcoDayChart({
  setpoint, hysteresis, lti, ventilation, schedule,
}: Readonly<EcoDayChartProps>): ReactElement {
  const { t } = useTranslation();
  const sp = setpoint ?? DESIGN_DEFAULTS.setpoint;
  const ltiValue = lti.value ?? DESIGN_DEFAULTS.lti;
  const geometry = computeEcoDayChart({
    window: schedule,
    ventStart: ventilation.start,
    ventEnd: ventilation.end,
    setpoint: sp,
    upperHysteresis: hysteresis.upper ?? DESIGN_DEFAULTS.hysteresis,
    lowerHysteresis: hysteresis.lower ?? DESIGN_DEFAULTS.hysteresis,
    lti: ltiValue,
  });
  const progWidth = geometry.progX2 - geometry.progX1;
  const ecoLegendX = lti.enabled ? 490 : 196;
  const caption = t(lti.enabled ? 'ecoSimplificadoDiaLegenda' : 'ecoSimplificadoDiaLegendaSemLti', {
    inicial: ventilation.start > 0 ? t('ecoSimplificadoDiaVentInicial', { min: ventilation.start }) : t('ecoSimplificadoDiaSemVentInicial'),
    final: ventilation.end > 0 ? t('ecoSimplificadoDiaVentFinal', { min: ventilation.end }) : t('ecoSimplificadoDiaSemVentFinal'),
    setpoint: sp,
    lti: ltiValue,
    interpolation: { escapeValue: false },
  });

  return (
    <svg viewBox="0 0 1280 700" preserveAspectRatio="xMidYMid meet" fontFamily="Inter, sans-serif" role="img" aria-label={t('ecoSimplificadoDiaTitulo')}>
      <rect x="0" y="0" width="1280" height="700" fill={WHITE} />
      <g stroke={STROKE} strokeWidth="1">
        <line x1="190" y1="154" x2="1030" y2="154" />
        <line x1="190" y1="194" x2="1030" y2="194" />
        <line x1="190" y1="234" x2="1030" y2="234" />
        <line x1="190" y1="274" x2="1030" y2="274" />
        <line x1="190" y1="314" x2="1030" y2="314" />
        <line x1="190" y1="354" x2="1030" y2="354" />
        <line x1="190" y1="430" x2="1030" y2="430" />
        <line x1="190" y1="465" x2="1030" y2="465" />
        <line x1="190" y1="500" x2="1030" y2="500" />
        <line x1="190" y1="535" x2="1030" y2="535" />
      </g>
      <text x="30" y="30" fontSize="20" fontWeight="600" fill={TEXT_STRONG}>{t('ecoSimplificadoDiaTitulo')}</text>
      {/* Faixa confortável, setpoint e LTI só dentro da programação. */}
      <rect x={geometry.progX1} y={geometry.upperY} width={progWidth} height={geometry.lowerY - geometry.upperY} fill={PRIMARY_LIGHTER} />
      {lti.enabled ? <rect x={geometry.progX1} y={geometry.ltiY} width={progWidth} height="20" fill={WARNING_LIGHTER} /> : null}
      <line x1={geometry.progX1} y1={geometry.upperY} x2={geometry.progX2} y2={geometry.upperY} stroke={PRIMARY_LIGHT} strokeWidth="1" />
      <line x1={geometry.progX1} y1={geometry.lowerY} x2={geometry.progX2} y2={geometry.lowerY} stroke={PRIMARY_LIGHT} strokeWidth="1" />
      <line x1={geometry.progX1} y1="230" x2={geometry.progX2} y2="230" stroke={TEXT_SOFT} strokeWidth="1" strokeDasharray="5 4" />
      {lti.enabled ? <line x1={geometry.progX1} y1={geometry.ltiY} x2={geometry.progX2} y2={geometry.ltiY} stroke={WARNING} strokeWidth="1.5" /> : null}
      <rect x={geometry.progX1} y="110" width={geometry.vent1W} height="425" fill={INFO} opacity="0.16" />
      <rect x={geometry.vent2X} y="110" width={geometry.vent2W} height="425" fill={INFO} opacity="0.16" />
      <line x1={geometry.progX1} y1="110" x2={geometry.progX1} y2="535" stroke={PRIMARY} strokeWidth="1" strokeDasharray="4 4" />
      <line x1={geometry.progX2} y1="110" x2={geometry.progX2} y2="535" stroke={PRIMARY} strokeWidth="1" strokeDasharray="4 4" />
      <text x={geometry.progX1 + 8} y="126" fontSize="12" fontWeight="600" fill={PRIMARY}>{t('ecoSimplificadoDiaEcoHabilitado')}</text>
      <text x={geometry.progX2 - 8} y="126" fontSize="12" fontWeight="600" fill={PRIMARY} textAnchor="end">{t('ecoSimplificadoDiaEcoDesabilitado')}</text>
      <path d={geometry.temperaturePath} fill="none" stroke={RED} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <path d={geometry.capacityPath} fill="none" stroke={PRIMARY} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      <line x1="190" y1="555" x2="1030" y2="555" stroke={STROKE} strokeWidth="1" />
      <g>
        {geometry.temperatureTicks.map((tick) => (
          <text key={tick.y} x="182" y={tick.y} fontSize="12" fill={TEXT_SOFT} textAnchor="end">{tick.label}</text>
        ))}
        {geometry.hourTicks.map((tick) => (
          <g key={tick.label}>
            <line x1={tick.x} y1="555" x2={tick.x} y2="561" stroke={TEXT_SOFT} />
            <text x={tick.x} y="575" fontSize="12" fill={TEXT_SOFT} textAnchor="middle">{tick.label}</text>
          </g>
        ))}
      </g>
      <text x="1042" y="434" fontSize="12" fill={TEXT_SUB}>{t('ecoSimplificadoDiaCapacidadeTotal')}</text>
      <text x="1042" y={geometry.labelY.upper} fontSize="12" fill={PRIMARY}>{t('ecoSimplificadoDiaSetpointHisteseSuperior')}</text>
      <text x="1042" y="234" fontSize="12" fill={TEXT_SUB}>{t('setpoint')}</text>
      <text x="1042" y={geometry.labelY.lower} fontSize="12" fill={PRIMARY}>{t('ecoSimplificadoDiaSetpointHisteseInferior')}</text>
      {lti.enabled ? <text x="1042" y={geometry.labelY.lti} fontSize="12" fill={WARNING_DARK}>{t('ecoSimplificadoDiaLti')}</text> : null}
      <text x="1042" y="469" fontSize="12" fill={TEXT_SUB}>{t('ecoSimplificadoDiaCapacidadeParcial')}</text>
      <text x="1042" y="504" fontSize="12" fill={TEXT_SUB}>{t('ventilacao')}</text>
      <text x="1042" y="539" fontSize="12" fill={TEXT_SUB}>{t('ecoSimplificadoDiaDesligado')}</text>
      <g>
        <line x1="190" y1="615" x2="214" y2="615" stroke={RED} strokeWidth="2.5" strokeLinecap="round" />
        <text x="222" y="619" fontSize="12" fill={TEXT_SUB}>{t('temperaturaAmbiente')}</text>
        <path d="M400,620 H406 V615 H412 V610 H418" fill="none" stroke={PRIMARY} strokeWidth="2.5" strokeLinejoin="round" />
        <text x="428" y="619" fontSize="12" fill={TEXT_SUB}>{t('ecoSimplificadoDiaCapacidadeMaquina')}</text>
        <rect x="620" y="609" width="18" height="12" fill={PRIMARY_LIGHTER} stroke={PRIMARY_LIGHT} />
        <text x="646" y="619" fontSize="12" fill={TEXT_SUB}>{t('ecoSimplificadoFaixaConfortavel')}</text>
        <rect x="790" y="609" width="18" height="12" fill={INFO} opacity=".5" />
        <text x="816" y="619" fontSize="12" fill={TEXT_SUB}>{t('ecoSimplificadoDiaVentilacaoInicialFinal')}</text>
        {lti.enabled ? (
          <>
            <line x1="190" y1="645" x2="214" y2="645" stroke={WARNING} strokeWidth="1.5" />
            <text x="222" y="649" fontSize="12" fill={TEXT_SUB}>{t('ecoSimplificadoDiaLimiteLti')}</text>
          </>
        ) : null}
        {/* Sem o LTI na legenda, o item seguinte ocupa o lugar dele. */}
        <line x1={ecoLegendX} y1="637" x2={ecoLegendX} y2="653" stroke={PRIMARY} strokeWidth="1" strokeDasharray="4 4" />
        <text x={ecoLegendX + 12} y="649" fontSize="12" fill={TEXT_SUB}>{t('ecoSimplificadoDiaInicioFimEco')}</text>
      </g>
      <text x="190" y="678" fontSize="12" fill={TEXT_SOFT}>{caption}</text>
    </svg>
  );
}
